import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { connect, useDispatch } from 'react-redux';
import { withRouter } from 'react-router-dom';

import PAGE_TYPES from 'tradera-constants/page-types';
import discoverActions from 'tradera-state/discover/actions';
import { debounce } from 'packages';
import { useTranslator, useUrlLocalizer } from 'tradera-lang';
import { getSuggestions, addRecentSearch } from './helpers/suggestions-api';
import {
    trackSearch,
    trackSearchCategoryChanges,
    trackResetButton
} from './helpers/search.analytics';
import {
    getNewSearchUrl,
    getCategoryDropdownConfig,
    getNextIndex
} from './helpers/helper';

import SearchCategory from 'components/search-category/search-category';
import SearchInput from './components/search-input';
import SearchSuggestion from './components/search-suggestion';

export const KEYCODE = {
    ENTER: 13,
    ESC: 27,
    UP: 38,
    DOWN: 40
};

export const MIN_CHARS_FOR_SUGGESTIONS = 2;

export const Search = ({
    inSPA,
    allCategories = [],
    categoryPath = [],
    inputClassName = '',
    discoverCurrentCategoryId = 0,
    discoverSearchTerm = '',
    queryParams,
    history
}) => {
    const dispatch = useDispatch();
    const { t } = useTranslator();
    const { toLocalizedUrl } = useUrlLocalizer();
    const searchSuggestionsElementRef = React.createRef();
    const searchInputRef = React.createRef();

    const [currentCategoryId, setCurrentCategoryId] = useState(
        discoverCurrentCategoryId
    );
    const [isSearchInputFocused, setIsSearchInputFocused] = useState(false);
    const [searchTerm, setSearchTerm] = useState(discoverSearchTerm || '');
    const [suggestions, setSuggestions] = useState([]);
    const [selectedSuggestionIndex, setSelectedSuggestionIndex] = useState(
        null
    );

    useEffect(() => {
        setCurrentCategoryId(discoverCurrentCategoryId);
    }, [discoverCurrentCategoryId]);

    useEffect(() => {
        setSearchTerm(discoverSearchTerm);
    }, [discoverSearchTerm]);

    const loadSuggestionsDebounce = debounce(async (searchTerm, callback) => {
        const suggestions = await getSuggestionsWrapper(searchTerm);
        if (suggestions) {
            callback(suggestions);
        }
    }, 200);

    /**
     * this function wraps the getSuggestions and makes sure the paramters to the getSuggestions are consistent
     * @param {string} searchTerm
     */
    const getSuggestionsWrapper = async searchTerm => {
        const enableSuggestionsFromBackend =
            searchTerm.length >= MIN_CHARS_FOR_SUGGESTIONS;
        const suggestions = await getSuggestions({
            searchTerm,
            limitPrevious: enableSuggestionsFromBackend ? 3 : 7,
            enablePrevious: true,
            enableSuggestions: enableSuggestionsFromBackend
        });
        return suggestions;
    };

    const handleUpDownArrowKeys = keyCode => {
        let incrementBy = null;
        if (keyCode === KEYCODE.DOWN) {
            incrementBy = 1;
        } else if (keyCode === KEYCODE.UP) {
            incrementBy = -1;
        }
        if (incrementBy !== null) {
            const newIndex = getNextIndex(
                incrementBy,
                selectedSuggestionIndex,
                suggestions.length
            );
            setSelectedSuggestionIndex(newIndex);
        }
    };

    const handleUserKeyUp = evt => {
        switch (evt.which) {
            case KEYCODE.ESC:
                evt.target.blur();
                return;

            case KEYCODE.ENTER:
                // eslint-disable-next-line no-case-declarations
                const suggestions = searchSuggestionsElementRef.current?.querySelectorAll(
                    '[data-suggestion]'
                );

                if (suggestions && suggestions[selectedSuggestionIndex]) {
                    evt.preventDefault();
                    suggestions[selectedSuggestionIndex].click();
                }
                return;

            case KEYCODE.DOWN:
            case KEYCODE.UP:
                handleUpDownArrowKeys(evt.which);
                return;
        }
    };

    const loadSearchSuggestions = async newSearchTerm => {
        setSelectedSuggestionIndex(null);
        setSearchTerm(newSearchTerm);
        setIsSearchInputFocused(true);

        if (newSearchTerm.length >= MIN_CHARS_FOR_SUGGESTIONS) {
            loadSuggestionsDebounce(newSearchTerm, suggestions => {
                setSuggestions(suggestions);
            });
            return;
        }
        const suggestions = await getSuggestionsWrapper(newSearchTerm);

        setSuggestions(suggestions);
    };

    const handleUserInputChange = evt =>
        loadSearchSuggestions(evt.target.value);

    const handleBlur = () => setIsSearchInputFocused(false);

    const handleFocus = () => {
        loadSearchSuggestions(searchTerm);
    };

    const handlePreviousSearchRemoved = () => {
        loadSearchSuggestions(searchTerm);
    };

    const handleCategoryChange = event => {
        const currentCategoryId = parseInt(event.target.value, 10);
        setCurrentCategoryId(currentCategoryId);
        dispatch(discoverActions.setCurrentCategoryId(currentCategoryId));
        trackSearchCategoryChanges(event);
    };

    const changeWindowLocation = newQuery => {
        // Trim search term to avoid empty searches
        const trimmedSearchTerm = newQuery.trim();
        let newSearchUrl = getNewSearchUrl(
            trimmedSearchTerm,
            queryParams,
            currentCategoryId,
            [...allCategories, ...categoryPath]
        );

        // Search with search term
        if (trimmedSearchTerm) {
            addRecentSearch(trimmedSearchTerm);
            trackSearch(trimmedSearchTerm);
        }
        if (inSPA) {
            searchInputRef.current.blur();
            history.push(toLocalizedUrl(newSearchUrl));
        } else {
            window.location.href = toLocalizedUrl(newSearchUrl);
        }
        setIsSearchInputFocused(false);
    };

    const handleFormSubmit = event => {
        event.preventDefault();
        changeWindowLocation(searchTerm);
    };

    const clearSearchInputAndGoToBrowse = event => {
        event.preventDefault();
        trackResetButton();
        setSearchTerm('');
        changeWindowLocation('');
    };

    const handleSuggestionsChangingSearchTerm = newSearchTerm => {
        // fixes case when user is on the same url as the current selected serach term. To reproduce:
        // 1. search tröja
        // 2. change the search term in the search box to "tröj" without hitting enter
        // 3. now you get previous searches "tröja", click on it
        // 4. without next line nothing will happen
        if (newSearchTerm !== searchTerm) {
            setSearchTerm(newSearchTerm);
        }
        setIsSearchInputFocused(false);
        searchInputRef.current.blur();
    };

    const {
        selectedOption: selectedCategory,
        optionsList: categorySelectOptions
    } = getCategoryDropdownConfig(
        t,
        allCategories,
        categoryPath,
        currentCategoryId
    );

    return (
        <SearchInput
            categoriesElement={
                <SearchCategory
                    handleCategoryChange={handleCategoryChange}
                    chosenCategory={selectedCategory}
                    selectedCategoryId={currentCategoryId}
                    selectOptions={categorySelectOptions}
                />
            }
            inputClassName={inputClassName}
            onBlur={handleBlur}
            onClearSearchInput={clearSearchInputAndGoToBrowse}
            onFocus={handleFocus}
            onFormSubmit={handleFormSubmit}
            onUserInputChange={handleUserInputChange}
            onUserKeyUp={handleUserKeyUp}
            searchInputRef={searchInputRef}
            searchTerm={searchTerm}
            selectedCategory={selectedCategory}
            suggestionsElement={
                <SearchSuggestion
                    suggestions={suggestions}
                    inSPA={inSPA}
                    visible={isSearchInputFocused}
                    onChangeSearchTerm={handleSuggestionsChangingSearchTerm}
                    onPreviousSearchRemoved={handlePreviousSearchRemoved}
                    searchTerm={searchTerm}
                    selectedCategoryId={currentCategoryId}
                    selectedIndex={selectedSuggestionIndex}
                    searchSuggestionsElementRef={searchSuggestionsElementRef}
                />
            }
        />
    );
};

const mapStateToProps = state => {
    const pageType = state.page.type;
    let categoryPath = undefined;
    let discoverCurrentCategoryId = 0;
    let discoverSearchTerm = '';

    // Reflect search state on search & category pages
    if (
        [
            PAGE_TYPES.SEARCH_RESULT_PAGE,
            PAGE_TYPES.CATEGORY_RESULT_PAGE
        ].includes(pageType)
    ) {
        categoryPath = state.discover.categoryPath;
        discoverCurrentCategoryId = state.discover.currentCategoryId;
        discoverSearchTerm = state.discover.queryParams?.q || '';
    }

    return {
        allCategories: state.page.allCategories,
        categories: state.categories,
        categoryPath,
        discoverCurrentCategoryId,
        queryParams: state.discover.queryParams,
        discoverSearchTerm
    };
};

Search.propTypes = {
    inSPA: PropTypes.bool.isRequired,
    allCategories: PropTypes.array,
    categoryPath: PropTypes.array,
    discoverCurrentCategoryId: PropTypes.number,
    queryParams: PropTypes.object
};

const ConnectedSearch = connect(mapStateToProps)(Search);
export const SearchInSpa = withRouter(ConnectedSearch);
export const SearchOutsideSpa = ConnectedSearch;
